import React, { Fragment, useEffect, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { Transition } from '@headlessui/react';
import {
  AditionInitialization,
  AditionTagPlacement,
  AditionTagWithFallback,
} from '@vgn-medien-holding/vgn-fe-components';
import { getCookie } from 'cookies-next';
import { Button } from '@components/atoms/Button/Button';
import { LoadingIndicator } from '@components/atoms/LoadingIndicator/LoadingIndicator';
import { Meta } from '@components/atoms/MetaTags/Meta';
import { Oewa } from '@components/atoms/Oewa/Oewa';
import { EntryCarousel } from '@components/molecules/EntryCarousel/EntryCarousel';
import { StreamingTeaser } from '@components/molecules/StreamingTeaser/StreamingTeaser';
import { Collection, GetBySlugDocument, GetTvmCollectionsDocument, Movie, Show } from '@src/lib/graphql/generated';
import { GetEntitiesDocument } from '@lib/graphql/generated';
import { createUrqlClient, usePaginatedQuery } from '@lib/graphql/urql';
import { useTagManager } from '@lib/hooks/useTagManager';
import { handleRedirect } from '@lib/redirects/redirect';

// Unterseite Collection
const ad_settings_UnterseiteCollection = {
  billboard: {
    id: 5256678,
    name: 'TVM_Unterseite_Collection_Billboard',
    yieldLab: [],
    breakpoints: [3, 4, 5, 6],
  },
  mobile_top: {
    id: 5256617,
    name: 'TVM_Mobile_Unterseite_Collection_HPA',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
  mobile_middle1: {
    id: 5256616,
    name: 'TVM_Mobile_Unterseite_Collection_MedRec_Cad',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
};

const shuffle = (array) => {
  if (!array) return;

  let currentIndex = array.length;
  let randomIndex;

  while (currentIndex != 0) {
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;
    [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];
  }

  return array;
};

export function CollectionDetails({ data }) {
  const { collections, oewa } = data;
  const [activeCollection, setActiveCollection] = useState<Collection>(data?.activeCollection);
  const [collectionTarget, setCollectionTarget] = useState(data?.activeCollection?.id || collections?.[0]);
  const router = useRouter();
  let ad_count = 0;

  useTagManager({
    event: 'metadata',
    loginStatus: 'nicht eingeloggt',
    seitentyp: 'Collection',
    titel: activeCollection?.title,
    assetID: oewa?.id,
    aktualisierungsdatum: data?.updated_at,
    erstellungsdatum: data?.created_at,
    oewaSeitenkontingent: oewa?.page_key || 'Service',
    oewaSubkontingent: oewa?.category_key || 'Programmeundlokalfuehrer',
    oewaProfilingkontingent: oewa?.profile_key || 'TVProgramme',
    cookieAlert: getCookie('OptanonConsent'),
  });

  useEffect(() => {
    if (router.isReady && activeCollection?.slug) {
      router.replace(
        {
          pathname: '/collections/[slug]',
          query: {
            slug: activeCollection?.slug,
          },
        },
        null,
        { shallow: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCollection]);

  const [
    {
      fetching: entitiesLoading,
      data: entitiesData,
      fetchSpecificPage: fetchEntitiesPage,
      hasMorePages: hasMoreEntities,
      fetchNextPage: fetchMoreEntities,
    },
  ] = usePaginatedQuery({
    query: GetEntitiesDocument,
    variables: {
      ids: activeCollection?.items,
      page: 1,
      first: 10,
    },
  });
  const entities = entitiesData?.flatMap((data) => data?.entities?.data);

  return (
    <>
      <Oewa
        pageKey={oewa?.page_key || 'RedCont'}
        categoryKey={oewa?.category_key || 'KulturUndFreizeit'}
        profileKey={oewa?.profile_key || 'FilmUndKino'}
      />
      <AditionInitialization data={{ ad_settings: ad_settings_UnterseiteCollection }} />
      <aside>
        <AditionTagPlacement
          tag={ad_settings_UnterseiteCollection.mobile_top}
          classProps={{ root: 'mb-4 grid place-items-center lg:hidden' }}
        />
      </aside>
      <Head>
        {Meta(
          {
            ...activeCollection?.metadata,
            open_graph_image:
              activeCollection?.random_item?.cover_image?.url ??
              activeCollection?.random_item?.backdrop_image?.url ??
              activeCollection?.metadata?.open_graph_image,
          },
          activeCollection?.title,
        )}
      </Head>
      <div className="grid">
        <div className="row-start-3 mt-12 grid min-w-0 gap-2 lg:row-start-auto lg:mt-0 lg:flex lg:flex-wrap lg:justify-center lg:gap-4 lg:px-3">
          <select
            className="input-tvm-classic lg:giant w-full max-w-full overflow-x-hidden"
            value={activeCollection?.id}
            onChange={(item) => {
              setCollectionTarget(item?.target.value);
            }}
          >
            {collections?.map((collection) => (
              <option className="w-full" key={collection.id} value={collection.id}>
                {collection.title}
              </option>
            ))}
          </select>
        </div>
        <div className="row-start-1 mt-1 min-w-0 lg:row-start-auto">
          {collections?.length > 0 && (
            <EntryCarousel
              target={collectionTarget}
              type="text"
              primaryProperty="title"
              entries={collections}
              classProps={{
                carouselRoot: 'aspect-square min-h-[300px] h-[30vh] sm:h-[60vh] lg:h-[70vh] portrait-giant:h-[640px]',
                entry: 'text-4xl sm:text-5xl md:text-6xl',
              }}
              onEntryChange={(collection) => {
                setCollectionTarget(null);
                if (collection) {
                  collection.items = shuffle(collection.items);
                }
                fetchEntitiesPage(1);
                setActiveCollection(collection);
              }}
            />
          )}
        </div>
        <div className="mx-auto mt-4 max-w-screen-md px-8 text-center sm:mt-0">
          <div
            className="prose-sm text-white"
            dangerouslySetInnerHTML={{ __html: activeCollection?.description }}
          ></div>
        </div>
      </div>
      <article className="mt-10">
        {entities?.length > 0 ? (
          <section>
            <div className="grid grid-flow-dense grid-cols-fill-streaming-default gap-x-4 gap-y-12">
              {entities?.map((entity, idx) => (
                <Fragment key={entity.id}>
                  {idx > 1 && idx % 8 === 0 && idx !== 16 && ad_count < 3 && (
                    <div className="col-span-full col-start-1 grid place-items-center">
                      <AditionTagWithFallback
                        tag={ad_settings_UnterseiteCollection.billboard}
                        tagCount={ad_count++}
                        fallback={ad_settings_UnterseiteCollection.mobile_middle1}
                        breakpoint={'lg'}
                      />
                    </div>
                  )}
                  <Transition
                    as="div"
                    enter="transition-opacity duration-500"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="transition-opacity duration-500"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    show={true}
                    appear={true}
                  >
                    <StreamingTeaser
                      entry={entity as Movie | Show}
                      listicleType={entity?.__typename?.toUpperCase() as 'MOVIE' | 'SHOW'}
                    />
                  </Transition>
                </Fragment>
              ))}
            </div>
            {entitiesLoading ? (
              <LoadingIndicator visible classProps={{ root: 'w-full h-96' }} />
            ) : (
              hasMoreEntities && (
                <div className="text-center">
                  <Button
                    title="Mehr laden"
                    hasAction
                    onAction={() => setTimeout(fetchMoreEntities)}
                    classProps={{ root: 'mt-20' }}
                    isLoading={entitiesLoading}
                    buttonStyle="secondary"
                  />
                </div>
              )
            )}
          </section>
        ) : !entitiesLoading ? (
          <div className="py-8 text-center text-2xl">Leider liegen für diese Auswahl momentan keine Inhalte vor.</div>
        ) : (
          <LoadingIndicator visible classProps={{ root: 'w-full h-96' }} />
        )}
      </article>
    </>
  );
}

export const collectionDetailsSsrLogic = async ({ slug }) => {
  const { ssrQuery, ssrCache } = createUrqlClient();
  const { data: collectionsData } = await ssrQuery({
    query: GetTvmCollectionsDocument,
    variables: {},
  });
  const collections = collectionsData?.collections;

  const filteredCollections = collections?.filter((e) => e.items && e.items.length > 0);
  const activeCollection = filteredCollections?.find((e) => e.slug === slug);

  if (!activeCollection) {
    // Check if a redirect is in place
    const { data: pageData } = await ssrQuery({
      query: GetBySlugDocument,
      variables: {
        portal: 'tvmedia',
        slug: `/collections/${slug}`,
      },
    });

    // Handle redirect result
    if (pageData?.findBySlug?.__typename === 'Redirect' && pageData?.findBySlug?.redirect_type) {
      return handleRedirect(
        pageData?.findBySlug?.redirect_type,
        pageData?.findBySlug?.redirect_target != null ? pageData?.findBySlug?.redirect_target : '',
      );
    }
  }

  if (!activeCollection) return { notFound: true };

  activeCollection.items = shuffle(activeCollection.items);

  await ssrQuery({
    query: GetEntitiesDocument,
    variables: {
      ids: activeCollection?.items,
      page: 1,
      first: 10,
    },
  });

  // Handle redirect result
  if (activeCollection?.metadata?.redirect) {
    return handleRedirect(301, activeCollection?.metadata?.redirect);
  }

  return {
    revalidate: 60 * 60 * 24 * 2, // 2 days
    props: {
      data: {
        collections: filteredCollections,
        activeCollection: activeCollection || null,
      },
      urqlState: ssrCache.extractData(),
    },
  };
};
